import {Amenities, Offer as SapiOffer} from '@findhotel/sapi'
import {MealPlan} from '@findhotel/sapi/dist/types/packages/core/src'

/**
 * Set of meal-related amenities available for hotel offers.
 */
export const MEAL_AMENITIES = new Set([
  'allInclusive',
  'breakfast',
  'partialBreakfast',
  'lunch',
  'dinner',
  'fullBoard',
  'halfBoard',
  'meals'
])

/**
 * A type that represents the available meals.
 * Excluding 'partialBreakfast' as it is not being used.
 */
export type MealAmenity = Exclude<
  Amenities,
  'internetIncluded' | 'partialBreakfast'
>

/**
 * A reusable function that returns all the available meals for any list of strings
 * @param list - any list of strings
 * @returns MealAmenity[]
 */
export const getMealsAmenitiesFromList = (list: Amenities[]): MealAmenity[] =>
  (list?.filter(amenity => MEAL_AMENITIES.has(amenity)) ?? []) as MealAmenity[]

/**
 * A factory function that returns all the available meals for each offer
 * @param offer - SapiOffer
 * @returns MealAmenity[]
 */
export const getMealsAmenities = (
  offer: Pick<SapiOffer, 'package'>
): MealAmenity[] => {
  return getMealsAmenitiesFromList(offer?.package?.amenities)
}

/**
 * A factory function that return ['allInclusive'] when the 'allInclusive' is presented inside meals list
 * @param meals - offer meals
 * @returns MealAmenity[]
 */
export const getPrioritizedMeals = (
  meals: MealAmenity[] | null | undefined
): MealAmenity[] => {
  if (meals?.some(meal => meal === 'allInclusive')) return ['allInclusive']
  return meals ?? []
}

/**
 * Meal amenities of an offer's meal plan, for the room labels.
 * @param mealPlan - offer meal plan
 * @returns MealAmenity[]
 */
export const getMealPlanAmenities = (
  mealPlan: MealPlan[] = []
): MealAmenity[] =>
  getMealsAmenitiesFromList(mealPlan.map(({meal}) => meal as Amenities))

/**
 * Meals that include breakfast
 */
export const BREAKFAST_INCLUDED_MEALS: readonly MealAmenity[] = [
  'breakfast',
  'halfBoard',
  'fullBoard',
  'allInclusive'
]

/**
 * Whether the offer's meal plan includes breakfast
 * @param mealPlan - offer meal plan
 * @returns true when any entry is a breakfast-bearing meal
 */
export const hasBreakfast = (mealPlan?: MealPlan[]): boolean =>
  getMealPlanAmenities(mealPlan).some(meal =>
    BREAKFAST_INCLUDED_MEALS.includes(meal)
  )
